import React, { useEffect, useState } from 'react';
import { Spinner, Badge } from 'react-bootstrap';
import { FaGraduationCap, FaUsers, FaChartBar, FaCheckCircle } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/apiClient';

const StatCard = ({ icon: Icon, label, value, gradient, shadow }) => (
  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 flex items-center gap-4 hover:shadow-xl transition-all">
    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md ${shadow}`}>
      <Icon className="w-7 h-7 text-white" />
    </div>
    <div>
      <div className="text-2xl font-extrabold text-gray-800">{value}</div>
      <div className="text-sm text-gray-500 font-medium">{label}</div>
    </div>
  </div>
);

const InstructorDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ courseCount: 0, userCount: 0, enrollmentStats: [], isOrgInstructor: false });

  useEffect(() => {
    api.instructor.getDashboard()
      .then((res) => { if (res.data?.success) setData(res.data.data); })
      .catch((err) => console.error('Dashboard error:', err))
      .finally(() => setLoading(false));
  }, []);

  const totalEnrollments = data.enrollmentStats.reduce((s, e) => s + e.total,     0);
  const totalCompleted   = data.enrollmentStats.reduce((s, e) => s + e.completed, 0);
  const completionRate   = totalEnrollments > 0 ? Math.round((totalCompleted / totalEnrollments) * 100) : 0;

  const greeting = user?.first_name ? `Welcome back, ${user.first_name}!` : 'Welcome back!';

  return (
    <div className="p-3 sm:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h4 className="text-2xl sm:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-red-600 mb-1">
          {greeting}
        </h4>
        <p className="text-sm text-gray-500">
          {data.isOrgInstructor
            ? 'Overview of your organization courses and assigned learners.'
            : 'Overview of your directly assigned courses and learners.'}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-24">
          <Spinner animation="border" variant="primary" style={{ width: 48, height: 48 }} />
        </div>
      ) : (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={FaGraduationCap} label="Assigned Courses"   value={data.courseCount}
              gradient="from-purple-500 to-purple-700"  shadow="shadow-purple-500/40" />
            <StatCard icon={FaUsers}         label="Assigned Learners"  value={data.userCount}
              gradient="from-blue-500 to-blue-700"      shadow="shadow-blue-500/40"   />
            <StatCard icon={FaChartBar}      label="Total Enrollments"  value={totalEnrollments}
              gradient="from-emerald-500 to-teal-600"   shadow="shadow-emerald-500/40" />
            <StatCard icon={FaCheckCircle}   label="Completion Rate"    value={`${completionRate}%`}
              gradient="from-violet-500 to-purple-700"  shadow="shadow-violet-500/40" />
          </div>

          {/* Course tracking table */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-red-600 px-5 py-3">
              <h6 className="text-white font-bold text-base mb-0">Course Tracking</h6>
            </div>
            {data.enrollmentStats.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FaGraduationCap size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No course data yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Course</th>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Total Enrolled</th>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Completed</th>
                      <th className="text-left px-5 py-3 font-semibold text-gray-600">Completion %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.enrollmentStats.map((stat) => {
                      const pct = stat.total > 0 ? Math.round((stat.completed / stat.total) * 100) : 0;
                      return (
                        <tr key={stat.course_id} className="border-b border-gray-50 hover:bg-purple-50/40">
                          <td className="px-5 py-3 font-medium text-gray-700">{stat.course_title || `Course #${stat.course_id}`}</td>
                          <td className="px-5 py-3 text-gray-600">{stat.total}</td>
                          <td className="px-5 py-3">
                            <Badge bg="success" className="px-2 py-1">{stat.completed}</Badge>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-2 max-w-[120px]">
                                <div
                                  className="bg-gradient-to-r from-purple-500 to-red-500 h-2 rounded-full transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium text-gray-600">{pct}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default InstructorDashboard;
